"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Card } from "@/components/ui/card";
import { ButtonLink } from "@/components/ui/button-link";
import { CheckCircle } from "lucide-react";
import Image from "next/image";

interface TwoColumnProps {
  title: string;
  description: string;
  bulletPoints?: string[];
  ctaText?: string;
  ctaHref?: string;
  imagePosition?: "left" | "right";
  image?: string;
}

const TwoColumn = ({
  title,
  description,
  bulletPoints,
  ctaText,
  ctaHref,
  imagePosition = "right",
  image,
}: TwoColumnProps) => {
  const textContent = (
    <BlurFade delay={0.1}>
      <div className="flex flex-col justify-center gap-6 h-full">
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
          {title}
        </h2>
        <p className="text-foreground/60 text-base md:text-lg leading-relaxed">
          {description}
        </p>
        {bulletPoints && bulletPoints.length > 0 && (
          <ul className="space-y-3">
            {bulletPoints.map((point, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle
                  size={20}
                  className="text-primary flex-shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="text-foreground/70 leading-relaxed">{point}</span>
              </li>
            ))}
          </ul>
        )}
        {ctaText && ctaHref && (
          <div className="pt-2">
            <ButtonLink
              href={ctaHref}
              variant="primary"
              size="lg"
              className="font-bold"
            >
              {ctaText}
            </ButtonLink>
          </div>
        )}
      </div>
    </BlurFade>
  );

  const imageContent = (
    <BlurFade delay={0.2}>
      {image ? (
        <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden">
          <Image
            src={image}
            alt={title}
            fill
            className="object-cover"
          />
        </div>
      ) : (
        <Card className="w-full aspect-[4/3] rounded-3xl bg-muted border-0 flex items-center justify-center overflow-hidden relative">
          {/* Decorative placeholder */}
          <div className="absolute inset-0 bg-gradient-to-br from-muted via-muted to-foreground/5" />
          <div className="relative z-10 flex flex-col items-center gap-4 text-foreground/20">
            <div className="w-20 h-20 rounded-full border-2 border-dashed border-foreground/10 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full bg-foreground/10" />
            </div>
            <div className="space-y-2 text-center">
              <div className="w-32 h-2 bg-foreground/10 rounded-full" />
              <div className="w-24 h-2 bg-foreground/5 rounded-full mx-auto" />
            </div>
          </div>
        </Card>
      )}
    </BlurFade>
  );

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {imagePosition === "left" ? (
            <>
              {imageContent}
              {textContent}
            </>
          ) : (
            <>
              {textContent}
              {imageContent}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default TwoColumn;
